import {
  Children,
  Fragment,
  Suspense,
  cloneElement,
  isValidElement,
  use,
  useEffect,
  useState,
  type ComponentProps,
  type ReactElement,
  type ReactNode,
} from "react";
import { Link } from "@tanstack/react-router";
import { JsonLd } from "@/components/seo/json-ld";
import { audience, blog, feature } from "../../lib/collections";
import { COVER_SIZE, THUMB_SIZE, longDate, type ArticleData } from "../../lib/article";
import { FIGURES, isFigureId } from "../../lib/figures";
import { blogHref, relForUrl } from "../../lib/blog-links";
import type { ContentPageData } from "../../lib/server";
import { getMDXComponents } from "../mdx";
import { Figure } from "./figure";
import { Toc, type TocItem } from "./toc";

const TAKEAWAYS_ID = "key-takeaways";
const FAQ_ID = "faq";
/** The waitlist's address as the FAQ answers write it; the visible answer links it. */
const WAITLIST_TEXT = "bavlio.com/waitlist";

/**
 * The article template, for any feature, audience or blog page whose
 * frontmatter says `template: article`. The title block (breadcrumb, status,
 * title, summary and the byline with its photo, Updated date and read time),
 * then the cover, the key takeaways, the MDX body with its charts, tables,
 * callouts and code, the questions, a closing card with the author and cards
 * for other pages. Beside it on a wide screen, "On this page" and the page's
 * action stay in view. On a phone, "On this page" opens from under the byline
 * and the action becomes a one-line bar along the bottom.
 *
 * Its colours are the Bavimail palette tokens (`--hb-*`), so it follows the
 * reader's saved light and dark palettes like the rest of the content site.
 */
export function ArticlePage({ data }: { data: ContentPageData }) {
  return (
    <Suspense fallback={<div className="p-8 text-hb-muted">Loading article...</div>}>
      <Body data={data} />
    </Suspense>
  );
}

function Body({ data }: { data: ContentPageData }) {
  const article = data.article!;
  const author = article.author;
  const collection =
    data.collection === "blog" ? blog : data.collection === "feature" ? feature : audience;
  const page = collection.getPage(data.path);
  if (!page) throw new Error(`Unknown article: ${data.path}`);
  const { toc } = use(page.load());
  const MDX = page.body;
  const items: TocItem[] = [
    { id: TAKEAWAYS_ID, label: "Key takeaways" },
    ...toc
      .filter((item) => item.depth === 2)
      .map((item) => ({ id: item.url.slice(1), label: item.title })),
    ...(data.faqs.length ? [{ id: FAQ_ID, label: "FAQ" }] : []),
  ];
  const crumbs = data.surface?.breadcrumbs ?? [];

  return (
    <main id="marketing-main" className="art">
      <JsonLd data={data.schemas.flat()} />
      <div className="art-page">
        <header className="art-head">
          <nav className="art-crumbs" aria-label="Breadcrumb">
            <ol>
              <li>
                <Link to="/" preload="intent">
                  Home
                </Link>
              </li>
              {crumbs.map((crumb) =>
                crumb.href ? (
                  <li key={crumb.label}>
                    <Link to={crumb.href} preload="intent">
                      {crumb.label}
                    </Link>
                  </li>
                ) : (
                  <li key={crumb.label} aria-current="page">
                    {article.crumb}
                  </li>
                ),
              )}
            </ol>
          </nav>
          {article.availability.length ? <Availability items={article.availability} /> : null}
          <h1>{data.title}</h1>
          {data.description ? <p className="art-summary">{data.description}</p> : null}
          <div className="art-byline">
            <img src={author.photo} alt="" width={44} height={44} />
            <p>
              <span className="art-byline-name">
                <a href={author.sameAs[0]} rel="author">
                  {author.name}
                </a>
                <span className="art-byline-role">, {author.role}</span>
              </span>
              <span className="art-byline-meta">
                Updated{" "}
                <time dateTime={article.dateModified}>{longDate(article.dateModified)}</time>
                <span aria-hidden="true"> · </span>
                {article.readMinutes} min read
              </span>
            </p>
          </div>
        </header>

        <aside className="art-rail">
          <div className="art-rail-inner">
            <Toc items={items} />
            <ActionBox action={article.action} />
          </div>
        </aside>

        <div className="art-body">
          <div className="art-cover">
            <img
              src={article.cover}
              alt={article.coverAlt}
              width={COVER_SIZE.width}
              height={COVER_SIZE.height}
              fetchPriority="high"
            />
          </div>

          <section className="art-takeaways" aria-labelledby={TAKEAWAYS_ID}>
            <h2 className="art-label" id={TAKEAWAYS_ID}>
              Key takeaways
            </h2>
            <ul>
              {article.takeaways.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>

          <article className="art-prose" aria-label={data.title}>
            <MDX components={articleComponents(data.collection === "blog")} />
          </article>

          {data.faqs.length ? (
            <section className="art-prose art-faq" aria-labelledby={FAQ_ID}>
              <h2 id={FAQ_ID}>FAQ</h2>
              {data.faqs.map((faq) => (
                <div className="art-faq-item" key={faq.question}>
                  <h3>{faq.question}</h3>
                  <p>
                    <Answer text={faq.answer} waitlist={article.action.href} />
                  </p>
                </div>
              ))}
            </section>
          ) : null}

          <aside className="art-end" aria-label="Author and next step">
            <img src={author.photo} alt={author.name} width={64} height={64} loading="lazy" />
            <div>
              <p className="art-end-title">{article.action.title}</p>
              <p className="art-end-byline">
                <a href={author.sameAs[0]} rel="author">
                  {author.name}
                </a>
                , {author.role}. {author.bio}
              </p>
            </div>
            <a className="art-button" href={article.action.href}>
              {article.action.label}
            </a>
          </aside>

          {article.related.length ? (
            <section className="art-related" aria-labelledby="art-related-title">
              <h2 id="art-related-title">Keep reading</h2>
              <ul>
                {article.related.map((card) => (
                  <li key={card.url}>
                    <Link className="art-card" to={card.url} preload="intent">
                      <img
                        src={card.thumb}
                        alt=""
                        width={THUMB_SIZE.width}
                        height={THUMB_SIZE.height}
                        loading="lazy"
                        decoding="async"
                      />
                      <span className="art-card-text">
                        <span className="art-label">{card.label}</span>
                        <span className="art-card-title">{card.title}</span>
                        {card.description ? (
                          <span className="art-card-description">{card.description}</span>
                        ) : null}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      </div>
    </main>
  );
}

const STATUS_KEY = { Live: "live", "Early access": "early", Waitlist: "waitlist" } as const;

/** Each channel's status, as a word with a mark beside it. */
function Availability({ items }: { items: ArticleData["availability"] }) {
  return (
    <ul className="art-status" aria-label="Status">
      {items.map((item) => (
        <li key={item.channel} data-status={STATUS_KEY[item.status]}>
          <span className="art-status-mark" aria-hidden="true" />
          {item.channel}: <strong>{item.status}</strong>
        </li>
      ))}
    </ul>
  );
}

/**
 * The page's action, beside the article on a wide screen and as a bar on a
 * phone. It steps aside while the closing card or the footer is on screen,
 * since the closing card carries the same button.
 */
function ActionBox({ action }: { action: ArticleData["action"] }) {
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    const targets = [document.querySelector(".art-end"), document.querySelector("footer")].filter(
      (target): target is Element => target !== null,
    );
    if (!targets.length || typeof IntersectionObserver === "undefined") return;
    const visible = new Set<Element>();
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) visible.add(entry.target);
        else visible.delete(entry.target);
      }
      setHidden(visible.size > 0);
    });
    for (const target of targets) observer.observe(target);
    return () => observer.disconnect();
  }, []);
  return (
    <div className="art-box" data-hidden={hidden ? "" : undefined} inert={hidden}>
      <p className="art-box-title">{action.title}</p>
      <p className="art-box-bar">{action.bar}</p>
      <a className="art-button" href={action.href}>
        {action.label}
      </a>
    </div>
  );
}

/** An FAQ answer, with the waitlist's address linked. The FAQPage schema keeps the same words. */
function Answer({ text, waitlist }: { text: string; waitlist: string }) {
  const parts = text.split(WAITLIST_TEXT);
  return parts.map((part, i) => (
    <Fragment key={i}>
      {part}
      {i < parts.length - 1 ? <a href={waitlist}>{WAITLIST_TEXT}</a> : null}
    </Fragment>
  ));
}

type WithChildren = { children?: ReactNode };

function textOf(node: ReactNode): string {
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textOf).join("");
  if (isValidElement<WithChildren>(node)) return textOf(node.props.children);
  return "";
}

/** Each body cell, labelled with its column's heading for the phone layout. */
function labelCells(row: ReactNode, headers: string[]): ReactNode {
  if (!isValidElement<WithChildren>(row)) return row;
  let column = 0;
  return cloneElement(
    row,
    {},
    Children.map(row.props.children, (cell) =>
      isValidElement(cell)
        ? cloneElement(cell as ReactElement<{ "data-label"?: string }>, {
            "data-label": headers[column++],
          })
        : cell,
    ),
  );
}

/**
 * A table. On a wide screen it scrolls sideways inside its own box when it
 * has to; on a phone each row becomes a card with every value under its
 * column's heading, so no column is hidden off the edge. A two-column card
 * reads as a name and its value, so it gets no labels: they would repeat
 * the same heading on every card.
 */
function Table({ children, ...props }: ComponentProps<"table">) {
  const parts = Children.toArray(children);
  const headers: string[] = [];
  for (const part of parts)
    if (isValidElement<WithChildren>(part) && part.type === "thead")
      Children.forEach(part.props.children, (row) => {
        if (isValidElement<WithChildren>(row))
          Children.forEach(row.props.children, (cell) => {
            if (isValidElement(cell)) headers.push(textOf(cell));
          });
      });
  return (
    <div className="art-table" role="region" aria-label={headers.join(", ")} tabIndex={0}>
      <table {...props}>
        {parts.map((part) =>
          isValidElement<WithChildren>(part) && part.type === "tbody" && headers.length > 2
            ? cloneElement(
                part,
                {},
                Children.map(part.props.children, (row) => labelCells(row, headers)),
              )
            : part,
        )}
      </table>
    </div>
  );
}

/**
 * Inline code that may wrap after a slash rather than inside a word, though
 * never inside or right after a "//", so "https://" stays with its host.
 */
function Code({ children, ...props }: ComponentProps<"code">) {
  if (typeof children !== "string" || !children.includes("/"))
    return <code {...props}>{children}</code>;
  const pieces = children.split("/");
  return (
    <code {...props}>
      {pieces.map((piece, i) => (
        <Fragment key={i}>
          {piece}
          {i < pieces.length - 1 ? "/" : null}
          {i < pieces.length - 1 && piece !== "" && pieces[i + 1] !== "" ? <wbr /> : null}
        </Fragment>
      ))}
    </code>
  );
}

const CALLOUT_LABELS: Record<string, string> = {
  info: "Note",
  warn: "Before you start",
  warning: "Before you start",
  error: "Watch out",
  success: "Live",
  idea: "Tip",
};

function Callout({
  type = "info",
  title,
  children,
}: {
  type?: string;
  title?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <aside className="art-callout" data-type={type}>
      <p className="art-label">{title ?? CALLOUT_LABELS[type] ?? "Note"}</p>
      <div className="art-callout-body">{children}</div>
    </aside>
  );
}

function FigureById({ id }: { id: string }) {
  if (!isFigureId(id)) throw new Error(`No figure "${id}" in lib/figures.ts`);
  return <Figure figure={FIGURES[id]} />;
}

/** A link in a blog post, resolved like the rest of the blog's links. */
function BlogLink({ href, children, ...props }: ComponentProps<"a">) {
  const target = href ? blogHref(href) : undefined;
  if (!target) return <span>{children}</span>;
  return (
    <a {...props} href={target} rel={relForUrl(target)}>
      {children}
    </a>
  );
}

function articleComponents(isBlog: boolean) {
  return getMDXComponents({
    table: Table,
    code: Code,
    Callout,
    Figure: FigureById,
    ...(isBlog ? { a: BlogLink } : {}),
  });
}
